/**
 * Styles for the certified-product record page (/p/:authId).
 *
 * The page is designed as a public register entry rather than a product page:
 * a plaster ground, one white "sheet" carrying the record, hairlines instead
 * of boxed cards, and roman-numbered sections. B-01 §26.2 forbids leaf, globe
 * and green-tick imagery, so nothing here draws one — the certification reads
 * as a typographic seal, not a badge.
 *
 * The red ink appears ONLY on the "what it does not certify" side, and only as
 * hairlines, numerals and one italic word. It is never a fill: those seven
 * rows are the honest limits of the mark, not an error state.
 */

// Plaster wall · paper · ink · correction ink · the brand evergreen.
const GROUND = "#F1EEE7";
const SHEET = "#FFFFFF";
const INK = "#191712";
const MUTED = "#5F5A4D";
const RULE = "#DCD6C9";
const GREEN = "#1B5E20";
const GREEN_HOVER = "#164C1A";
const GREEN_RULE = "#3D7541";
const GREEN_PALE = "#C8DAC6";
const GREEN_TEXT = "#E4EEE3";
const RED_INK = "#8A2B22";

const DISPLAY = "'Newsreader', Georgia, 'Times New Roman', serif";
const BODY =
  "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace";

// One breakpoint. Below it the page is a single column; at or above it the
// sheet splits into a sticky rail and a 720px measure, which is where long
// body copy stops running to 1000px lines.
const DESKTOP = "@media (min-width: 1120px)";

// Second breakpoint, for the report band alone. Below it the sheet is narrower
// than the viewport by exactly the band's padding, so the band can be pulled
// full-bleed with a negative margin. Above it the measure is capped at 720px
// and that same margin would end mid-air, so the band becomes an inset.
const WIDE = "@media (min-width: 760px)";

/**
 * The design language of the certification record, exported so the pages that
 * belong to the same journey — the record itself and the failure page a bad
 * scan lands on — are built from one set of values rather than two that drift.
 */
export const tokens = {
  GROUND, SHEET, INK, MUTED, RULE, GREEN, GREEN_HOVER, RED_INK,
  DISPLAY, BODY, MONO, DESKTOP,
};

const label = {
  fontFamily: MONO,
  fontSize: "10px",
  fontWeight: 500,
  letterSpacing: "0.14em",
  lineHeight: "14px",
  color: MUTED,
  margin: 0,
};

/** Page shell and language toggle — shared with the failure page. */
export const shell = {
  /* ---------------------------------------------------------------- shell */
  page: {
    background: GROUND,
    color: INK,
    fontFamily: BODY,
    minHeight: "100vh",
    // The site-wide Header is `fixed` and about 86px tall, so it overlays the
    // top of the page. Clear it outright rather than relying on the masthead's
    // own padding to make up the difference.
    paddingTop: "90px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    zIndex: 3,
  },
  band: {
    display: "flex",
    justifyContent: "center",
    padding: "0 20px",
    [DESKTOP]: { padding: "0 60px" },
  },
  inner: {
    width: "100%",
    maxWidth: "1160px",
    minWidth: 0,
  },
  // Applied to the band that holds a loading or error message: without it the
  // footer floats halfway up a short page.
  bandGrow: { flexGrow: 1 },

  /* ---------------------------------------------------------- lang toggle */
  // Right-aligned in the masthead's eyebrow row when the record renders, and
  // in a row of its own while the page is loading or has failed.
  langRow: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: "20px",
  },
  langToggle: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${RULE}`,
    borderRadius: "999px",
    padding: "3px",
    background: SHEET,
    cursor: "pointer",
    flexShrink: 0,
  },
  langOn: {
    fontFamily: MONO,
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: SHEET,
    background: INK,
    borderRadius: "999px",
    padding: "5px 11px",
  },
  langOff: {
    fontFamily: MONO,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.08em",
    color: MUTED,
    padding: "5px 11px",
  },


  /* --------------------------------------------------------- company line */
  // The shared Footer carries the links and the copyright but not the
  // registered company details, which a certification record should state.
  companyLine: {
    fontFamily: BODY,
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: "21px",
    color: MUTED,
    margin: 0,
    padding: "28px 0 36px",
    borderTop: `1px solid ${RULE}`,
    [DESKTOP]: { padding: "32px 0 40px" },
  },
};

const productPageStyle = {
  ...shell,

  /* ------------------------------------------------------------- masthead */
  masthead: {
    padding: "12px 0 26px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    [DESKTOP]: {
      padding: "40px 0 68px",
      flexDirection: "row",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: "72px",
    },
  },
  mastheadText: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    minWidth: 0,
    [DESKTOP]: { flex: "1 1 auto", gap: "22px" },
  },
  eyebrow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    [DESKTOP]: { gap: "10px" },
  },
  eyebrowRule: {
    width: "18px",
    height: "1px",
    background: GREEN,
    flexShrink: 0,
    [DESKTOP]: { width: "26px" },
  },
  eyebrowText: {
    ...label,
    color: GREEN,
    [DESKTOP]: { fontSize: "11px" },
  },
  productName: {
    fontFamily: DISPLAY,
    fontSize: "34px",
    fontWeight: 500,
    letterSpacing: "-0.02em",
    lineHeight: "38px",
    color: INK,
    margin: 0,
    [DESKTOP]: {
      fontSize: "60px",
      lineHeight: "62px",
      letterSpacing: "-0.025em",
      maxWidth: "660px",
    },
  },
  identityRow: {
    display: "flex",
    alignItems: "stretch",
    gap: "16px",
    paddingTop: "8px",
    [DESKTOP]: { display: "none" },
  },
  productPhoto: {
    width: "96px",
    height: "96px",
    flexShrink: 0,
    background: SHEET,
    border: `1px solid ${RULE}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
    boxSizing: "border-box",
  },
  productPhotoDesktop: {
    display: "none",
    [DESKTOP]: {
      display: "flex",
      width: "272px",
      height: "272px",
      flexShrink: 0,
      background: SHEET,
      border: `1px solid ${RULE}`,
      alignItems: "center",
      justifyContent: "center",
      padding: "26px",
      boxSizing: "border-box",
    },
  },
  productPhotoImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  identityMeta: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "12px",
    flex: "1 1 auto",
    minWidth: 0,
  },
  metaBlock: { display: "flex", flexDirection: "column", gap: "4px" },
  metaLabel: { ...label },
  metaValue: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "20px",
    color: INK,
    margin: 0,
  },
  metaValueMono: {
    fontFamily: MONO,
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "18px",
    color: INK,
    margin: 0,
    wordBreak: "break-all",
  },
  metaRow: {
    display: "none",
    [DESKTOP]: {
      display: "flex",
      alignItems: "flex-start",
      gap: "56px",
      borderTop: `1px solid ${RULE}`,
      paddingTop: "20px",
      marginTop: "6px",
    },
  },
  metaRowBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flexShrink: 0,
  },
  metaRowValue: {
    fontFamily: BODY,
    fontSize: "17px",
    fontWeight: 500,
    lineHeight: "24px",
    color: INK,
    margin: 0,
  },
  metaRowValueMono: {
    fontFamily: MONO,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "24px",
    color: INK,
    margin: 0,
  },

  /* ----------------------------------------------------------------- seal */
  seal: { background: GREEN },
  sealInner: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    padding: "26px 0 24px",
    [DESKTOP]: {
      flexDirection: "row",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: "64px",
      padding: "44px 0",
    },
  },
  sealStatement: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
    [DESKTOP]: { flex: "1 1 auto", gap: "12px" },
  },
  sealLabel: {
    ...label,
    color: GREEN_PALE,
    [DESKTOP]: { fontSize: "11px" },
  },
  sealHeading: {
    fontFamily: DISPLAY,
    fontSize: "30px",
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: "34px",
    color: SHEET,
    margin: 0,
    [DESKTOP]: { fontSize: "46px", lineHeight: "50px", letterSpacing: "-0.02em" },
  },
  sealFacts: {
    display: "flex",
    flexDirection: "column",
    borderTop: `1px solid ${GREEN_RULE}`,
    // Label left, value right works on a phone; across a whole tablet the two
    // stop reading as one row.
    maxWidth: "420px",
    [DESKTOP]: {
      alignItems: "flex-end",
      flexShrink: 0,
      borderTop: "none",
      borderLeft: `1px solid ${GREEN_RULE}`,
      paddingLeft: "56px",
    },
  },
  sealFactRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
    padding: "14px 0",
    borderBottom: `1px solid ${GREEN_RULE}`,
    "&:last-child": { borderBottom: "none", paddingBottom: 0 },
    [DESKTOP]: {
      flexDirection: "column",
      alignItems: "flex-end",
      gap: "10px",
      padding: 0,
      borderBottom: "none",
    },
  },
  // The record id lives in the masthead's meta row on desktop; repeating it
  // inside the seal would say the same thing twice on one screen.
  sealFactRowId: {
    [DESKTOP]: { display: "none" },
  },
  sealFactLabel: {
    ...label,
    color: GREEN_PALE,
    flexShrink: 0,
    [DESKTOP]: { fontSize: "11px" },
  },
  sealFactValue: {
    fontFamily: MONO,
    fontSize: "17px",
    fontWeight: 500,
    lineHeight: "20px",
    color: SHEET,
    textAlign: "right",
    margin: 0,
    [DESKTOP]: { fontSize: "30px", lineHeight: "36px", letterSpacing: "-0.01em" },
  },
  sealFactValueSmall: {
    fontFamily: MONO,
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: "20px",
    color: GREEN_TEXT,
    textAlign: "right",
    wordBreak: "break-all",
    margin: 0,
  },

  /* ---------------------------------------------------------------- sheet */
  sheet: { background: SHEET, flex: "1 1 auto" },
  sheetInner: {
    display: "flex",
    flexDirection: "column",
    [DESKTOP]: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: "80px",
      padding: "72px 0 40px",
    },
  },

  /* ----------------------------------------------------------------- rail */
  rail: {
    display: "none",
    [DESKTOP]: {
      display: "flex",
      flexDirection: "column",
      width: "260px",
      flexShrink: 0,
      position: "sticky",
      top: "32px",
      alignSelf: "flex-start",
    },
  },
  railLabel: { ...label, paddingBottom: "18px" },
  railItem: {
    border: 0,
    borderTop: `1px solid ${RULE}`,
    display: "flex",
    alignItems: "baseline",
    gap: "14px",
    padding: "11px 0 11px 16px",
    background: "none",
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    "&:last-of-type": { borderBottom: `1px solid ${RULE}` },
  },
  railItemActive: {
    borderLeft: `2px solid ${GREEN}`,
    paddingLeft: "14px",
  },
  railNum: {
    width: "22px",
    flexShrink: 0,
    fontFamily: MONO,
    fontSize: "10px",
    fontWeight: 500,
    letterSpacing: "0.12em",
    lineHeight: "20px",
    color: MUTED,
  },
  railNumActive: { color: GREEN },
  railText: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "20px",
    color: MUTED,
  },
  railTextActive: { fontWeight: 500, color: INK },
  railBrand: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingTop: "36px",
  },
  railBrandLogo: {
    width: "108px",
    height: "34px",
    objectFit: "contain",
    objectPosition: "left center",
  },

  /* -------------------------------------------------------------- content */
  content: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    // Without the cap, a 1000px-wide window would set body copy in 960px
    // lines — the exact fault this redesign set out to fix on desktop.
    maxWidth: "720px",
    // `flex` rather than a fixed width: between 1120px and 1180px the rail,
    // the gap and a rigid 720px measure add up to more than the sheet, and a
    // fixed width would push the page into horizontal scroll.
    [DESKTOP]: { flex: "1 1 auto", maxWidth: "720px" },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "34px 0 32px",
    [DESKTOP]: { gap: "20px", padding: "0 0 56px" },
  },
  sectionHead: {
    display: "flex",
    alignItems: "baseline",
    gap: "12px",
    [DESKTOP]: { gap: "16px" },
  },
  sectionNum: {
    width: "22px",
    flexShrink: 0,
    fontFamily: MONO,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.14em",
    lineHeight: "16px",
    color: MUTED,
    [DESKTOP]: { width: "30px", fontSize: "12px", lineHeight: "20px" },
  },
  sectionNumRed: { color: RED_INK },
  sectionTitle: {
    fontFamily: DISPLAY,
    fontSize: "25px",
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: "30px",
    color: INK,
    margin: 0,
    flex: "1 1 auto",
    [DESKTOP]: { fontSize: "34px", lineHeight: "40px", letterSpacing: "-0.02em" },
  },
  // Three nodes on one baseline: the middle word is the page's single wink,
  // and rich text inside one heading node is not an option here.
  titleEm: {
    fontStyle: "italic",
    color: RED_INK,
  },
  sectionAside: {
    fontFamily: MONO,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.1em",
    lineHeight: "16px",
    color: MUTED,
    flexShrink: 0,
    [DESKTOP]: { fontSize: "12px", lineHeight: "20px" },
  },
  sectionBody: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    paddingLeft: "34px",
    [DESKTOP]: { gap: "20px", paddingLeft: "46px" },
  },
  paragraph: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "26px",
    color: INK,
    margin: 0,
    wordBreak: "break-word",
    [DESKTOP]: { fontSize: "17px", lineHeight: "29px" },
  },
  // A lead paragraph that sits directly under a section head, sharing the
  // numeral lane's indent without a wrapping body block.
  sectionIntro: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "26px",
    color: INK,
    margin: 0,
    paddingLeft: "34px",
    [DESKTOP]: { fontSize: "17px", lineHeight: "29px", paddingLeft: "46px" },
  },
  paragraphMuted: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "24px",
    color: MUTED,
    margin: 0,
    paddingLeft: "34px",
    [DESKTOP]: { fontSize: "16px", lineHeight: "26px", paddingLeft: "46px" },
  },

  /* ---------------------------------------------------------------- scope */
  scope: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    background: GROUND,
    padding: "16px 18px",
    borderLeft: `2px solid ${GREEN}`,
    [DESKTOP]: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: "28px",
      padding: "22px 26px",
    },
  },
  scopeLabel: {
    ...label,
    [DESKTOP]: { width: "108px", flexShrink: 0, lineHeight: "26px" },
  },
  scopeValue: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "24px",
    color: INK,
    margin: 0,
    [DESKTOP]: { fontSize: "16px", lineHeight: "26px", flex: "1 1 auto" },
  },

  /* --------------------------------------------------------------- ledger */
  ledger: {
    display: "flex",
    flexDirection: "column",
    paddingTop: "4px",
    [DESKTOP]: { paddingLeft: "46px", paddingTop: 0 },
  },
  ledgerRow: {
    display: "flex",
    gap: "12px",
    padding: "15px 0",
    borderTop: `1px solid ${RULE}`,
    "&:last-child": { borderBottom: `1px solid ${RULE}` },
    [DESKTOP]: { alignItems: "flex-start", gap: "20px", padding: "16px 0" },
  },
  ledgerNum: {
    width: "22px",
    flexShrink: 0,
    fontFamily: MONO,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.1em",
    lineHeight: "22px",
    color: RED_INK,
    [DESKTOP]: { width: "26px", lineHeight: "25px" },
  },
  // The claim's ledger: same rows as the limits, but the numerals carry the
  // mark's green — red ink stays reserved for the honest boundary.
  ledgerNumGreen: {
    color: GREEN,
  },
  ledgerCell: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    flex: "1 1 auto",
    minWidth: 0,
    [DESKTOP]: { flexDirection: "row", alignItems: "flex-start", gap: "20px" },
  },
  ledgerClaim: {
    fontFamily: BODY,
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "22px",
    color: INK,
    margin: 0,
    [DESKTOP]: { width: "240px", flexShrink: 0, lineHeight: "25px" },
  },
  ledgerWhy: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "23px",
    color: MUTED,
    margin: 0,
    [DESKTOP]: { flex: "1 1 auto", minWidth: 0, lineHeight: "25px" },
  },

  /* ---------------------------------------------------------- report band */
  reportBand: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    background: GROUND,
    padding: "28px 20px",
    borderTop: `1px solid ${RULE}`,
    borderBottom: `1px solid ${RULE}`,
    // Full-bleed on a phone: the sheet's own padding is on the sections, so
    // the band is pulled out to the viewport edges to read as a break.
    marginLeft: "-20px",
    marginRight: "-20px",
    [WIDE]: {
      margin: "0 0 0 34px",
      padding: "28px 30px",
      border: "none",
      borderLeft: `2px solid ${RED_INK}`,
    },
    [DESKTOP]: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: "40px",
      margin: "0 0 56px 46px",
      padding: "32px 34px",
      border: "none",
      borderLeft: `2px solid ${RED_INK}`,
    },
  },
  reportText: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flex: "1 1 auto",
    minWidth: 0,
  },
  reportHeading: {
    fontFamily: DISPLAY,
    fontSize: "21px",
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: "26px",
    color: INK,
    margin: 0,
    [DESKTOP]: { fontSize: "23px", lineHeight: "29px" },
  },
  reportBody: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "24px",
    color: MUTED,
    margin: 0,
  },
  reportEmailRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    flexWrap: "wrap",
    paddingTop: "4px",
  },
  reportEmailLabel: {
    fontFamily: BODY,
    fontSize: "14px",
    lineHeight: "20px",
    color: MUTED,
  },
  reportEmailLink: {
    fontFamily: MONO,
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "20px",
    color: GREEN,
  },
  reportButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: GREEN,
    color: SHEET,
    border: "none",
    borderRadius: 0,
    padding: "15px 24px",
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: "20px",
    textAlign: "center",
    cursor: "pointer",
    "&:hover": { background: GREEN_HOVER },
    "&:disabled": { opacity: 0.5, cursor: "default" },
    [DESKTOP]: { width: "210px", flexShrink: 0 },
  },

  /* ---------------------------------------------------------- ingredients */
  inciList: {
    fontFamily: BODY,
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "30px",
    color: INK,
    margin: 0,
    wordBreak: "break-word",
    [DESKTOP]: { fontSize: "21px", lineHeight: "34px" },
  },
  inciFooter: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    [DESKTOP]: {
      flexDirection: "row-reverse",
      alignItems: "flex-start",
      gap: "28px",
      borderTop: `1px solid ${RULE}`,
      paddingTop: "18px",
    },
  },
  inciNote: {
    fontFamily: BODY,
    fontSize: "13px",
    fontStyle: "italic",
    fontWeight: 400,
    lineHeight: "21px",
    color: MUTED,
    margin: 0,
    borderTop: `1px solid ${RULE}`,
    paddingTop: "14px",
    [DESKTOP]: { flex: "1 1 auto", borderTop: "none", paddingTop: 0 },
  },
  copyButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    alignSelf: "flex-start",
    background: "transparent",
    border: `1px solid ${RULE}`,
    borderRadius: 0,
    padding: "9px 14px",
    fontFamily: BODY,
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "18px",
    color: GREEN,
    cursor: "pointer",
    "&:hover": { borderColor: GREEN },
    [DESKTOP]: { flexShrink: 0 },
  },
  copyIcon: { flexShrink: 0 },

  /* -------------------------------------------------------------- process */
  steps: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: "34px",
    [DESKTOP]: { paddingLeft: "46px" },
  },
  stepRow: {
    display: "flex",
    gap: "12px",
    padding: "12px 0",
    borderTop: `1px solid ${RULE}`,
    "&:last-child": { borderBottom: `1px solid ${RULE}` },
    [DESKTOP]: { alignItems: "flex-start", gap: "20px", padding: "13px 0" },
  },
  stepNum: {
    width: "22px",
    flexShrink: 0,
    fontFamily: MONO,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.1em",
    lineHeight: "22px",
    color: GREEN,
    [DESKTOP]: { width: "26px", lineHeight: "24px" },
  },
  stepCell: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    flex: "1 1 auto",
    minWidth: 0,
    [DESKTOP]: { flexDirection: "row", alignItems: "flex-start", gap: "20px" },
  },
  stepText: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "22px",
    color: INK,
    margin: 0,
    [DESKTOP]: {
      fontSize: "16px",
      lineHeight: "24px",
      flex: "1 1 auto",
      minWidth: 0,
    },
  },
  stepActor: {
    fontFamily: MONO,
    fontSize: "10px",
    fontWeight: 500,
    letterSpacing: "0.12em",
    lineHeight: "14px",
    color: MUTED,
    margin: 0,
    [DESKTOP]: {
      width: "176px",
      flexShrink: 0,
      lineHeight: "24px",
      textAlign: "right",
    },
  },
  noteList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingLeft: "34px",
    [DESKTOP]: { gap: "9px", paddingLeft: "46px" },
  },
  noteRow: { display: "flex", gap: "10px", [DESKTOP]: { gap: "12px" } },
  noteDash: {
    width: "10px",
    flexShrink: 0,
    fontFamily: BODY,
    fontSize: "13px",
    lineHeight: "21px",
    color: MUTED,
    [DESKTOP]: { width: "12px" },
  },
  noteText: {
    fontFamily: BODY,
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: "21px",
    color: MUTED,
    margin: 0,
    flex: "1 1 auto",
  },

  /* ------------------------------------------------------------ documents */
  docList: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: "34px",
    [DESKTOP]: { paddingLeft: "46px" },
  },
  docRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "15px 0",
    borderTop: `1px solid ${RULE}`,
    textDecoration: "none",
    color: INK,
    "&:last-child": { borderBottom: `1px solid ${RULE}` },
    "&:hover $docTitle": { color: GREEN },
    [DESKTOP]: { gap: "20px", padding: "16px 0" },
  },
  docCell: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    flex: "1 1 auto",
    minWidth: 0,
    // Stacked on a phone (title, then kind); on desktop the kind becomes a
    // left-hand column, so `order` swaps them without a second markup path.
    [DESKTOP]: { flexDirection: "row", alignItems: "center", gap: "20px" },
  },
  docTitle: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 500,
    lineHeight: "21px",
    color: INK,
    margin: 0,
    [DESKTOP]: {
      order: 2,
      fontSize: "16px",
      lineHeight: "22px",
      flex: "1 1 auto",
      minWidth: 0,
    },
  },
  docKind: {
    ...label,
    [DESKTOP]: { order: 1, width: "120px", flexShrink: 0, lineHeight: "22px" },
  },
  docOpen: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    flexShrink: 0,
    [DESKTOP]: { order: 3, width: "96px" },
  },
  docOpenText: {
    display: "none",
    [DESKTOP]: {
      display: "block",
      fontFamily: BODY,
      fontSize: "13px",
      fontWeight: 600,
      lineHeight: "22px",
      color: GREEN,
    },
  },

  /* -------------------------------------------------------- states/dialog */
  stateBox: {
    background: SHEET,
    padding: "64px 24px",
    textAlign: "center",
    color: MUTED,
    fontFamily: BODY,
    fontSize: "16px",
    lineHeight: "26px",
    marginTop: "32px",
    marginBottom: "32px",
  },
  stateTitle: {
    fontFamily: DISPLAY,
    fontSize: "25px",
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: "30px",
    color: INK,
    margin: "0 0 10px",
  },
  dialogPaper: {
    background: SHEET,
    borderRadius: 0,
    border: `1px solid ${RULE}`,
  },
  dialogTitle: {
    fontFamily: DISPLAY,
    fontSize: "25px",
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: "30px",
    color: INK,
    margin: 0,
  },
  dialogIntro: {
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 400,
    lineHeight: "24px",
    color: MUTED,
    margin: 0,
  },
  dialogField: {
    width: "100%",
    fontFamily: BODY,
    fontSize: "15px",
    lineHeight: "24px",
    color: INK,
    padding: "10px 12px",
    border: `1px solid ${RULE}`,
    borderRadius: 0,
    marginTop: "6px",
    boxSizing: "border-box",
    background: SHEET,
    "&:focus": { outline: "none", borderColor: GREEN },
  },
  dialogLabel: {
    ...label,
    display: "block",
    marginTop: "18px",
  },
  dialogHelp: {
    fontFamily: BODY,
    fontSize: "13px",
    lineHeight: "20px",
    color: MUTED,
    marginTop: "6px",
  },
  dialogError: {
    fontFamily: BODY,
    fontSize: "14px",
    lineHeight: "22px",
    color: RED_INK,
    marginTop: "14px",
  },
  dialogHoneypot: {
    position: "absolute",
    left: "-9999px",
    width: "1px",
    height: "1px",
    overflow: "hidden",
  },
  dialogCancelButton: {
    background: "transparent",
    border: `1px solid ${RULE}`,
    borderRadius: 0,
    color: MUTED,
    padding: "14px 20px",
    fontFamily: BODY,
    fontSize: "15px",
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": { borderColor: INK, color: INK },
  },
};

export default productPageStyle;
